import React, { useEffect, useContext, useState } from 'react';
import { AppContext } from '../../../context/Context';
import { SocketContext } from '../../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import toast from 'react-hot-toast';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon, // in case you use it later
} from '@mui/icons-material/';
import {
  Tabs,
  Tab,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Skeleton,
  Paper,
} from '@mui/material';

const SkeletonTable = ({ rows = 8, cols = 4 }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        {Array.from({ length: cols }).map((_, idx) => (
          <TableCell key={idx}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {Array.from({ length: rows }).map((_, rIdx) => (
        <TableRow key={rIdx}>
          {Array.from({ length: cols }).map((_, cIdx) => (
            <TableCell key={cIdx}>
              <Skeleton />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

function ModalUserLimitStatus() {
  const { t } = useTranslation();

  const {
    updateShowModalUserLimitStatus,
    updateFocusOnMainScreen,
    userInfo,
    parentInfo,
    hall,
  } = useContext(AppContext);

  const { socket } = useContext(SocketContext);

  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0 = limits, 1 = transactions
  const [limits, setLimits] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // Disable focus on main screen, prevent escape, and request data
  useEffect(() => {
    setIsLoading(true);

    socket.emit('userLimitStatus', {
      userID: parentInfo ? parentInfo.id : userInfo.id,
      bingoName: hall,
    });

    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [socket, parentInfo, userInfo, hall, updateFocusOnMainScreen]);

  // Socket setup
  useEffect(() => {
    const handleUserLimitStatus = (data) => {
      setIsLoading(false);

      if (!data) {
        toast.error(t('error.error'));
        return;
      }

      setLimits(data.limits || []);
      setTransactions(data.transactions || []);
    };

    socket.on('userLimitStatus', handleUserLimitStatus);

    return () => {
      socket.off('userLimitStatus', handleUserLimitStatus);
    };
  }, [socket, t]);

  const handleConfirm = () => {
    updateShowModalUserLimitStatus(false);
  };

  const renderLimitsTable = () => {
    if (isLoading) return <SkeletonTable rows={10} cols={4} />;

    if (!limits.length) {
      return <Box p={2}>{t('sales.noData')}</Box>;
    }

    return (
      <Table
        size="small"
        sx={{
          '& thead th': {
            fontWeight: 'bold',
            backgroundColor: '#f0f0f0',
          },
          '& tbody tr:nth-of-type(odd)': {
            backgroundColor: 'rgba(0,0,0,0.02)', // striped rows
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.date')}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.cashin')}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.cashout')}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.rollover')}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.totalSpentDay')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {limits.map((row) => (
            <TableRow key={row.date}>
              <TableCell align="center" style={{ color: '#000' }}>
                {new Date(row.date).toLocaleDateString('no-NO')}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {row.cashin}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {row.cashout}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {row.rollover}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {parseInt(row.cashin) - parseInt(row.cashout) <= 0
                  ? 0
                  : parseInt(row.cashin) - parseInt(row.cashout)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderTransactionsTable = () => {
    if (isLoading) return <SkeletonTable rows={5} cols={4} />;

    if (!transactions.length) {
      return <Box p={2}>{t('sales.noData')}</Box>;
    }

    return (
      <Table
        size="small"
        sx={{
          '& thead th': {
            fontWeight: 'bold',
            backgroundColor: '#f0f0f0',
          },
          '& tbody tr:nth-of-type(odd)': {
            backgroundColor: 'rgba(0,0,0,0.02)',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.provider')}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.description')}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {t('sales.amount')}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {'chID'}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {'wgID'}
            </TableCell>
            <TableCell align="center" style={{ color: '#000' }}>
              {'okID'}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((tx, idx) => (
            <TableRow key={idx}>
              <TableCell align="center" style={{ color: '#000' }}>
                {tx.provider}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {tx.description}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {tx.amount}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {tx.chID === '' || tx.chID === 0 ? '-' : tx.chID}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {tx.wgID === '' || tx.wgID === 0 ? '-' : tx.wgID}
              </TableCell>
              <TableCell align="center" style={{ color: '#000' }}>
                {tx.okID === '' || tx.okID === 0 ? '-' : tx.okID}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          <div
            className="react-confirm-alert-body"
            style={{ width: '1200px', height: '800px', color: '#000' }}
          >
            <Paper
              elevation={3}
              sx={{
                backgroundColor: 'white',
                padding: 2,
                borderRadius: 2,
                height: '550px',
                color: 'black', // force text to be dark inside the paper
              }}
            >
              <Tabs
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                variant="fullWidth"
              >
                <Tab label={t('sales.limitsPerDay')} sx={{ color: 'grey' }} />
                <Tab label={t('sales.transactions')} sx={{ color: 'grey' }} />
              </Tabs>

              <Box mt={2} sx={{ maxHeight: 425, overflow: 'auto' }}>
                {tab === 0 && renderLimitsTable()}
                {tab === 1 && renderTransactionsTable()}
              </Box>
            </Paper>
            <div
              className="react-confirm-alert-button-group"
              style={{ marginTop: '55px' }}
            >
              <button
                className="btn-custom-yes"
                onClick={handleConfirm}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                {t('sales.confirm')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalUserLimitStatus;
